import type { SxProps, Theme } from '@mui/material/styles';

/**
 * The shared fleetGUARDIAN DataGridPro look, applied per-grid via `sx`.
 * Signature: 11px dense text, uppercase gray-500 headers on a light header bar,
 * hidden column separators, no internal cell borders, and the SOFT-GREEN zebra
 * (`#f0fdf4` even rows / `#dcfce7` hover) on white.
 *
 * Delivered as a theme callback so the dark toggle stays coherent — the source
 * app is light-only, so the dark variants here are Fuel-Bench-specific (subtle
 * white/blue washes instead of the green zebra, which only reads on white).
 */
export const dataGridSx: SxProps<Theme> = (theme: Theme) => {
  const dark = theme.palette.mode === 'dark';
  const border = dark ? 'rgba(255,255,255,0.10)' : '#e5e7eb';
  const headerBg = dark ? '#111a2e' : '#f9fafb';
  const headerFg = dark ? '#94a3b8' : '#6b7280';
  const cellFg = dark ? '#e2e8f0' : '#111827';
  const surface = dark ? 'transparent' : '#fff';
  const zebra = dark ? 'rgba(255,255,255,0.03)' : '#f0fdf4';
  const hover = dark ? 'rgba(59,130,246,0.12)' : '#dcfce7';

  return {
    fontSize: '11px',
    border: `1px solid ${border}`,
    borderRadius: '8px',
    backgroundColor: surface,

    '& .MuiDataGrid-columnHeaders': {
      backgroundColor: headerBg,
      fontWeight: 600,
      fontSize: '11px',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      color: headerFg,
      borderBottom: `1px solid ${border}`,
    },
    '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 600, fontSize: '11px' },
    '& .MuiDataGrid-columnSeparator': { display: 'none' },

    '& .MuiDataGrid-cell': {
      py: 0.5,
      fontSize: '11px',
      color: cellFg,
      borderBottom: 'none',
      borderRight: 'none',
    },

    // Soft-green zebra + green hover (the fleetGUARDIAN grid signature)
    '& .MuiDataGrid-row': { backgroundColor: dark ? 'transparent' : '#ffffff' },
    '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: zebra },
    '& .MuiDataGrid-row:hover': { backgroundColor: hover },

    '& .MuiDataGrid-footerContainer': {
      borderTop: `1px solid ${border}`,
      backgroundColor: headerBg,
    },
    '& .MuiDataGrid-row--lastVisible .MuiDataGrid-cell': { borderBottom: 'none' },

    // Toolbar quick-filter reads on both themes
    '& .MuiDataGrid-toolbarContainer': { padding: '8px', gap: '8px' },
  };
};

/** Default props every fleetGUARDIAN grid spreads. */
export const dataGridDefaults = {
  density: 'compact' as const,
  disableRowSelectionOnClick: true,
  hideFooterSelectedRowCount: true,
  pageSizeOptions: [10, 20, 50, 100],
};
